// Cliente openapi-fetch da API GIYUS com os middlewares transversais (Parte 13.1 / 14.2):
// bearer, X-Correlation-Id, Accept-Language, X-Purpose, Idempotency-Key e problem+json → GiyusApiError.
import createClient, { type Client, type Middleware } from 'openapi-fetch';

import { GiyusNetworkError, isProblemJson, toGiyusApiError } from './errors';
import {
  CORRELATION_ID_PATTERN,
  type DeclaredPurpose,
  HEADERS,
  MUTATING_METHODS,
  formatPurposeHeader,
} from './headers';
import type { paths } from './schema';

export type GiyusClient = Client<paths>;

export interface GiyusClientOptions {
  /** Base da API, ex.: `https://api-dev.giyus.com.br` (o `/api/v1` faz parte das rotas do contrato). */
  readonly baseUrl: string;
  /** Token de acesso atual (ou `null` quando anônimo). */
  readonly getAccessToken?: () => Promise<string | null> | string | null;
  /** Idioma da UI enviado em `Accept-Language` (troca sem recarregar, REQ-FE-005). */
  readonly getLocale: () => string;
  /** Propósito declarado vigente para o contexto (Parte 14.2); `null` quando não há. */
  readonly getPurpose?: () => DeclaredPurpose | null;
  /** Gerador de identificadores (correlação e idempotência); padrão `crypto.randomUUID`. */
  readonly generateId?: () => string;
  /** Callback acionado em `401 Unauthorized` (exceto `step_up_required`) para retornar à tela de login. */
  readonly onUnauthorized?: () => void;
  /** `fetch` customizado (testes). */
  readonly fetch?: typeof globalThis.fetch;
}

const defaultGenerateId = (): string => crypto.randomUUID();

/** Middleware que garante `X-Correlation-Id` válido em toda requisição. */
export function correlationMiddleware(generateId: () => string): Middleware {
  return {
    onRequest({ request }) {
      const current = request.headers.get(HEADERS.correlationId);
      if (!current || !CORRELATION_ID_PATTERN.test(current)) {
        request.headers.set(HEADERS.correlationId, generateId());
      }
      return request;
    },
  };
}

/** Middleware que envia o idioma da UI. */
export function acceptLanguageMiddleware(getLocale: () => string): Middleware {
  return {
    onRequest({ request }) {
      request.headers.set(HEADERS.acceptLanguage, getLocale());
      return request;
    },
  };
}

/** Middleware Bearer: só adiciona o cabeçalho quando há token. */
export function bearerMiddleware(
  getAccessToken: NonNullable<GiyusClientOptions['getAccessToken']>,
): Middleware {
  return {
    async onRequest({ request }) {
      if (request.headers.has(HEADERS.authorization)) {
        return request;
      }
      const token = await getAccessToken();
      if (token) {
        request.headers.set(HEADERS.authorization, `Bearer ${token}`);
      }
      return request;
    },
  };
}

/** Middleware `X-Purpose` (não sobrescreve um propósito explícito da chamada). */
export function purposeMiddleware(getPurpose: () => DeclaredPurpose | null): Middleware {
  return {
    onRequest({ request }) {
      if (request.headers.has(HEADERS.purpose)) {
        return request;
      }
      const purpose = getPurpose();
      if (purpose) {
        request.headers.set(HEADERS.purpose, formatPurposeHeader(purpose));
      }
      return request;
    },
  };
}

/** Middleware `Idempotency-Key` em comandos (POST/PUT/PATCH/DELETE) sem chave explícita. */
export function idempotencyMiddleware(generateId: () => string): Middleware {
  return {
    onRequest({ request }) {
      if (
        MUTATING_METHODS.has(request.method.toUpperCase()) &&
        !request.headers.has(HEADERS.idempotencyKey)
      ) {
        request.headers.set(HEADERS.idempotencyKey, generateId());
      }
      return request;
    },
  };
}

/** Middleware que converte respostas de erro em `GiyusApiError` e falhas de rede em `GiyusNetworkError`. */
export function createProblemDetailsMiddleware(onUnauthorized?: () => void): Middleware {
  return {
    async onResponse({ response }) {
      if (response.ok) {
        return response;
      }
      if (isProblemJson(response) || response.status >= 400) {
        const apiError = await toGiyusApiError(response);
        if (
          apiError.status === 401 &&
          apiError.errorCode !== 'PLT_STEP_UP_REQUIRED' &&
          apiError.problem['error'] !== 'step_up_required'
        ) {
          onUnauthorized?.();
        }
        throw apiError;
      }
      return response;
    },
    onError({ error, request }) {
      throw new GiyusNetworkError(error, request.headers.get(HEADERS.correlationId) ?? undefined);
    },
  };
}

export const problemDetailsMiddleware: Middleware = createProblemDetailsMiddleware();

/** Cria o cliente tipado da API com todos os middlewares transversais registrados. */
export function createGiyusClient(options: GiyusClientOptions): GiyusClient {
  const generateId = options.generateId ?? defaultGenerateId;
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  client.use(correlationMiddleware(generateId));
  client.use(acceptLanguageMiddleware(options.getLocale));
  if (options.getAccessToken) {
    client.use(bearerMiddleware(options.getAccessToken));
  }
  if (options.getPurpose) {
    client.use(purposeMiddleware(options.getPurpose));
  }
  client.use(idempotencyMiddleware(generateId));
  client.use(
    options.onUnauthorized
      ? createProblemDetailsMiddleware(options.onUnauthorized)
      : problemDetailsMiddleware,
  );

  return client;
}
